import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Avatar } from "@/components/Avatar";
import { api } from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { ExportButton, RangeSelect, num, useRange } from "./shared";

const Stat = ({ label, value, sub, testid }) => (
  <div className="tc-stat" data-testid={testid}><div className="tc-stat__label">{label}</div><div className="tc-stat__value">{value}</div>{sub && <div className="tc-stat__sub">{sub}</div>}</div>
);

export const Overview = () => {
  const { range, setRange, params } = useRange("7d");
  const [data, setData] = useState(null);
  const [series, setSeries] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/reports/overview", { params }).then((r) => setData(r.data)).catch(() => {});
    api.get("/reports/timeseries", { params: { ...params, interval: range === "24h" ? "hour" : "day" } }).then((r) => setSeries(r.data)).catch(() => {});
  }, [params, range]);
  const t = data?.totals || {};
  return (
    <div data-testid="admin-overview">
      <h1>Analytics overview</h1>
      <p className="lead">Every click, message, reaction, huddle, search and login across Total Quality Insurance — captured per user, in real time.</p>
      <div className="tc-admin__toolbar"><RangeSelect range={range} setRange={setRange} /><span className="grow" /><ExportButton params={params} /></div>
      <div className="tc-stats">
        <Stat label="Tracked events" value={num(t.events)} sub="client + server + API" testid="stat-events" />
        <Stat label="Clicks" value={num(t.clicks)} sub="every UI click" testid="stat-clicks" />
        <Stat label="Messages sent" value={num(t.messages)} sub={`${num(t.total_messages)} all time`} testid="stat-messages" />
        <Stat label="Reactions" value={num(t.reactions)} testid="stat-reactions" />
        <Stat label="Active users" value={num(t.active_users)} sub={`of ${num(t.total_users)} members`} testid="stat-active-users" />
        <Stat label="Sessions" value={num(t.sessions)} testid="stat-sessions" />
        <Stat label="Page views" value={num(t.page_views)} testid="stat-page-views" />
        <Stat label="Huddles" value={num(t.huddles)} sub={`${t.huddle_minutes ?? 0} minutes`} testid="stat-huddles" />
      </div>
      <div className="tc-admin-grid2">
        <div className="tc-admin-card">
          <div className="tc-admin-card__head">Activity over time<span className="right">{range === "24h" ? "hourly" : "daily"}</span></div>
          <div className="tc-admin-card__body" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="gEvents" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#4c6b93" stopOpacity={0.5} /><stop offset="100%" stopColor="#4c6b93" stopOpacity={0.02} /></linearGradient>
                  <linearGradient id="gMsgs" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#007a5a" stopOpacity={0.5} /><stop offset="100%" stopColor="#007a5a" stopOpacity={0.02} /></linearGradient>
                </defs>
                <CartesianGrid stroke="var(--tc-divider)" vertical={false} />
                <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "#616061" }} tickFormatter={(v) => v.slice(5, 16).replace("T", " ")} />
                <YAxis tick={{ fontSize: 11, fill: "#616061" }} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, fontFamily: "inherit", borderRadius: 6, background: "var(--tc-bg-elev)", border: "1px solid var(--tc-border)", color: "var(--tc-text)" }} />
                <Area type="monotone" dataKey="events" stroke="#4c6b93" fill="url(#gEvents)" strokeWidth={2} name="Events" />
                <Area type="monotone" dataKey="messages" stroke="#007a5a" fill="url(#gMsgs)" strokeWidth={2} name="Messages" />
                <Area type="monotone" dataKey="active_users" stroke="#92220c" fill="none" strokeWidth={2} name="Active users" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="tc-admin-card">
          <div className="tc-admin-card__head">Top event types</div>
          <div className="tc-admin-card__body" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.top_events?.slice(0, 10) || []} layout="vertical" margin={{ top: 0, right: 16, left: 20, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="event_type" width={130} tick={{ fontSize: 11, fill: "var(--tc-text)" }} />
                <Tooltip contentStyle={{ fontSize: 12, fontFamily: "inherit", borderRadius: 6, background: "var(--tc-bg-elev)", border: "1px solid var(--tc-border)", color: "var(--tc-text)" }} />
                <Bar dataKey="count" fill="#83388a" radius={[0, 4, 4, 0]} name="Events" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="tc-admin-grid2">
        <div className="tc-admin-card">
          <div className="tc-admin-card__head">Most active people<span className="right"><button className="tc-btn tc-btn--ghost sm" onClick={() => navigate("/admin/users")}>View all</button></span></div>
          <table className="tc-table" data-testid="top-users-table">
            <thead><tr><th>Member</th><th className="num">Events</th><th>Last active</th></tr></thead>
            <tbody>{(data?.top_users || []).map((r) => (
              <tr key={r.user?.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/events?user_id=${r.user?.id}`)}>
                <td><div className="usercell"><Avatar user={r.user} size={24} radius={5} /><div><div>{r.user?.display_name}</div><div className="sub">{r.user?.chat_email}</div></div></div></td>
                <td className="num">{num(r.count)}</td><td className="muted">{r.last_active ? fmtRelative(r.last_active) : "—"}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
        <div className="tc-admin-card">
          <div className="tc-admin-card__head">Most clicked UI elements</div>
          <table className="tc-table" data-testid="top-targets-table">
            <thead><tr><th>Element (data-testid)</th><th className="num">Clicks</th></tr></thead>
            <tbody>{(data?.top_click_targets || []).map((r) => <tr key={r.target}><td className="mono">{r.target}</td><td className="num">{num(r.count)}</td></tr>)}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
